import i18n from "../../core/i18n";
/**
 * Etichetta mostrata nel conto alla rovescia, la stessa usata per le ads IMA
 */
export function getAdLabel() {
    return i18n.t("advertisement", "Pubblicità");
}
// Se l'mp4 non parte entro questo tempo lo considero fallito, cosi' il contenuto non resta bloccato
const AUTOPROMOTION_START_TIMEOUT_MS = 10000;
export class AdsAutopromotions {
    /**
     * Costruttore della classe AdsAutopromotions
     * @param _adsClass - istanza Ads (non serve piu', resta per compatibilita' con il costruttore legacy)
     * @param autopromotions - autopromozioni del gruppo (ads_url = mp4 da riprodurre)
     */
    constructor(_adsClass, autopromotions) {
        this.autoPromotionsPosition = 0;
        this.overlay = null;
        this.videoElement = null;
        this.controls = null;
        this.startTimeout = null;
        this.hiddenControlBarPlayer = null;
        this.autoPromotions = (Array.isArray(autopromotions) ? autopromotions : [])
            .map((autopromotion) => String(autopromotion?.ads_url ?? "").trim())
            .filter((url) => url !== "");
    }
    /**
     * True se il gruppo ha almeno un'autopromo riproducibile
     */
    hasAutopromotions() {
        return this.autoPromotions.length > 0;
    }
    /**
     * Ritorna la source dell'autopromozione corrente
     */
    getAutoPromotionSource() {
        return this.autoPromotions[this.autoPromotionsPosition] ?? "";
    }
    /**
     * True mentre l'overlay dell'autopromo e' a schermo
     */
    isPlaying() {
        return this.overlay !== null;
    }
    /**
     * Riproduce l'autopromo in un <video> separato sopra il player.
     * Prima veniva cambiata la src del player principale: la live si fermava, il VOD andava
     * ricaricato e qualunque re-render React rimetteva la src del contenuto a meta' autopromo.
     * Con un elemento separato il contenuto non viene mai toccato.
     */
    play(player, callbacks) {
        this.stop();
        const source = this.getAutoPromotionSource();
        const playerElement = player?.el?.() ?? null;
        if (source === "" || playerElement === null) {
            callbacks.onError?.();
            return;
        }
        // Alla prossima autopromo passo alla successiva della lista
        this.autoPromotionsPosition =
            (this.autoPromotionsPosition + 1) % this.autoPromotions.length;
        const overlay = document.createElement("div");
        overlay.className = "autopromotion-overlay";
        Object.assign(overlay.style, {
            position: "absolute",
            inset: "0",
            width: "100%",
            height: "100%",
            background: "#000",
            // sopra filler gif (4) e loader (10), sotto il container IMA (1111)
            zIndex: "1100",
        });
        const videoElement = document.createElement("video");
        videoElement.setAttribute("playsinline", "");
        videoElement.playsInline = true;
        videoElement.preload = "auto";
        videoElement.controls = false;
        videoElement.volume =
            callbacks.volume > 0 ? Math.min(callbacks.volume, 1) : 1;
        videoElement.muted = callbacks.volume <= 0;
        Object.assign(videoElement.style, {
            width: "100%",
            height: "100%",
            objectFit: "contain",
        });
        videoElement.src = source;
        overlay.appendChild(videoElement);
        // Stessi controlli delle ads IMA: barra di avanzamento in sola lettura, pausa, volume e fullscreen
        const controls = this.createControls(player, overlay, videoElement);
        overlay.appendChild(controls.element);
        playerElement.appendChild(overlay);
        // La control bar del contenuto non serve: i controlli dell'autopromo sono nell'overlay
        player.controlBar?.hide?.();
        this.hiddenControlBarPlayer = player;
        this.overlay = overlay;
        this.videoElement = videoElement;
        this.controls = controls;
        let started = false;
        let finished = false;
        const finish = (callback) => {
            if (finished) {
                return;
            }
            finished = true;
            this.stop();
            callback?.();
        };
        videoElement.addEventListener("playing", () => {
            if (started) {
                return;
            }
            started = true;
            this.clearStartTimeout();
            this.dispatchRewardsEvent(player, "loaded", "INIZIATA AUTOPROMOTION");
            callbacks.onStart?.();
        });
        videoElement.addEventListener("ended", () => {
            this.dispatchRewardsEvent(player, "allAdsCompleted", "FINITA AUTOPROMOTION");
            finish(callbacks.onEnd);
        });
        videoElement.addEventListener("error", () => {
            console.error("Errore riproduzione autopromo:", source);
            finish(callbacks.onError);
        });
        this.startTimeout = window.setTimeout(() => {
            if (!started) {
                console.warn("Autopromo non partita entro il timeout:", source);
                finish(callbacks.onError);
            }
        }, AUTOPROMOTION_START_TIMEOUT_MS);
        videoElement.play().catch(() => {
            // Play con audio bloccato dal browser (mid-roll senza gesto recente): riprovo muto, come fa IMA
            if (finished) {
                return;
            }
            videoElement.muted = true;
            videoElement.play().catch(() => finish(callbacks.onError));
        });
    }
    /**
     * Toglie l'overlay e ripristina la control bar del player
     */
    stop() {
        this.clearStartTimeout();
        this.controls?.cleanup();
        this.controls = null;
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.removeAttribute("src");
            this.videoElement.load();
            this.videoElement = null;
        }
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
        if (this.hiddenControlBarPlayer) {
            this.hiddenControlBarPlayer.controlBar?.show?.();
            this.hiddenControlBarPlayer = null;
        }
    }
    destroy() {
        this.stop();
    }
    /**
     * Controlli dell'autopromo con le stesse classi (e quindi lo stesso aspetto) dei controlli delle
     * ads IMA di videojs-ima: conto alla rovescia, barra di avanzamento non trascinabile, play/pausa,
     * muto, volume e fullscreen del player
     */
    createControls(player, overlay, videoElement) {
        const createDiv = (className) => {
            const element = document.createElement("div");
            element.className = className;
            return element;
        };
        const createButton = (className, label) => {
            const element = createDiv(className);
            element.setAttribute("role", "button");
            element.setAttribute("aria-label", label);
            element.title = label;
            return element;
        };
        const controlsDiv = createDiv("ima-controls-div");
        controlsDiv.style.width = "100%";
        controlsDiv.style.display = "block";
        const countdownDiv = createDiv("ima-countdown-div");
        const seekBarDiv = createDiv("ima-seek-bar-div");
        seekBarDiv.style.width = "100%";
        const progressDiv = createDiv("ima-progress-div");
        seekBarDiv.appendChild(progressDiv);
        const playPauseDiv = createButton("ima-play-pause-div ima-playing", "Play");
        const muteDiv = createButton("ima-mute-div ima-non-muted", "Mute");
        const sliderDiv = createDiv("ima-slider-div");
        const sliderLevelDiv = createDiv("ima-slider-level-div");
        sliderDiv.appendChild(sliderLevelDiv);
        const fullscreenDiv = createButton("ima-fullscreen-div ima-non-fullscreen", "Fullscreen");
        // su iOS il volume non si puo' impostare da codice: come IMA lascio solo il muto
        if (typeof isIos === "function" && isIos()) {
            sliderDiv.style.display = "none";
        }
        controlsDiv.append(countdownDiv, seekBarDiv, playPauseDiv, muteDiv, sliderDiv, fullscreenDiv);
        const setClass = (element, active, activeClass, inactiveClass) => {
            element.classList.toggle(activeClass, active);
            element.classList.toggle(inactiveClass, !active);
        };
        const updatePlayPause = () => setClass(playPauseDiv, !videoElement.paused, "ima-playing", "ima-paused");
        const updateVolume = () => {
            const muted = videoElement.muted || videoElement.volume === 0;
            setClass(muteDiv, muted, "ima-muted", "ima-non-muted");
            sliderLevelDiv.style.width =
                (muted ? 0 : videoElement.volume * 100) + "%";
        };
        const updateProgress = () => {
            const duration = videoElement.duration;
            if (!Number.isFinite(duration) || duration <= 0) {
                countdownDiv.textContent = getAdLabel();
                return;
            }
            const currentTime = Math.min(videoElement.currentTime, duration);
            const remaining = Math.max(0, Math.ceil(duration - currentTime));
            const minutes = Math.floor(remaining / 60);
            const seconds = String(remaining % 60).padStart(2, "0");
            progressDiv.style.width = (currentTime / duration) * 100 + "%";
            countdownDiv.textContent = getAdLabel() + ": " + minutes + ":" + seconds;
        };
        const updateFullscreen = () => setClass(fullscreenDiv, !!player.isFullscreen?.(), "ima-fullscreen", "ima-non-fullscreen");
        // la barra resta in sola lettura: nessun listener su seekBarDiv
        const onPlayPauseClick = (event) => {
            event.stopPropagation();
            if (videoElement.paused) {
                videoElement.play().catch(() => {
                    // play bloccato: resta in pausa
                });
            }
            else {
                videoElement.pause();
            }
        };
        const onMuteClick = (event) => {
            event.stopPropagation();
            videoElement.muted = !videoElement.muted;
            if (!videoElement.muted && videoElement.volume === 0) {
                videoElement.volume = 1;
            }
        };
        const onSliderClick = (event) => {
            event.stopPropagation();
            const rect = sliderDiv.getBoundingClientRect();
            const percent = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            videoElement.volume = percent;
            videoElement.muted = percent === 0;
        };
        const onFullscreenClick = (event) => {
            event.stopPropagation();
            if (player.isFullscreen?.()) {
                player.exitFullscreen?.();
            }
            else {
                player.requestFullscreen?.();
            }
        };
        // come IMA: su desktop i pulsanti compaiono al passaggio del mouse, su touch restano visibili
        const isTouchDevice = window.matchMedia?.("(hover: none)").matches ?? false;
        const showControls = () => controlsDiv.classList.add("ima-controls-div-showing");
        const hideControls = () => controlsDiv.classList.remove("ima-controls-div-showing");
        if (isTouchDevice) {
            showControls();
        }
        else {
            overlay.addEventListener("mouseenter", showControls);
            overlay.addEventListener("mouseleave", hideControls);
        }
        playPauseDiv.addEventListener("click", onPlayPauseClick);
        muteDiv.addEventListener("click", onMuteClick);
        sliderDiv.addEventListener("click", onSliderClick);
        fullscreenDiv.addEventListener("click", onFullscreenClick);
        videoElement.addEventListener("play", updatePlayPause);
        videoElement.addEventListener("pause", updatePlayPause);
        videoElement.addEventListener("volumechange", updateVolume);
        videoElement.addEventListener("timeupdate", updateProgress);
        videoElement.addEventListener("durationchange", updateProgress);
        player.on?.("fullscreenchange", updateFullscreen);
        updatePlayPause();
        updateVolume();
        updateProgress();
        updateFullscreen();
        return {
            element: controlsDiv,
            cleanup: () => {
                // gli altri listener spariscono con gli elementi dell'overlay, quello sul player no
                player.off?.("fullscreenchange", updateFullscreen);
            },
        };
    }
    clearStartTimeout() {
        if (this.startTimeout !== null) {
            clearTimeout(this.startTimeout);
            this.startTimeout = null;
        }
    }
    /**
     * Gestione pubblicita' per guadagno coin (coinShop.js): l'evento serve solo ai player
     * con la classe player_ADV_rewards, come nel legacy
     */
    dispatchRewardsEvent(player, type, message) {
        if (!player?.hasClass?.("player_ADV_rewards")) {
            return;
        }
        window.dispatchEvent(new CustomEvent("ads_player_event", {
            detail: { type, message },
        }));
    }
}
